import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

export default function AdminExpertsManager() {
  const [users, setUsers] = useState([]);
  const [emailsById, setEmailsById] = useState({});
  const [roleHistory, setRoleHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  
  // État pour la confirmation
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    userId: null,
    newRole: null,
    userName: '',
    oldRole: ''
  });

const [showDemo] = useState(true); // Afficher par défaut


  useEffect(() => {
    loadAllUsers();
    loadRoleHistory();
    getCurrentAdmin();
  }, []);

  async function getCurrentAdmin() {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', session.user.id)
        .single();
      setCurrentAdmin(profile);
    }
  }

async function loadAllUsers() {
  setLoading(true);
  try {
    // RPC admin-gated : joint profiles + auth.users → email réel côté serveur.
    // (auth.admin.listUsers() exige la clé service_role, indisponible au navigateur.)
    const { data: rows, error: rpcError } = await supabase.rpc('admin_list_users');

    if (rpcError) throw rpcError;

    const usersWithEmail = (rows || []).map(row => ({
      ...row,
      email: row.email || 'Email non disponible',
      full_name: row.first_name && row.last_name
        ? `${row.first_name} ${row.last_name}`
        : row.first_name || row.last_name || row.email
    }));
    setUsers(usersWithEmail);

    // Table id → email réutilisée par l'historique des rôles.
    const map = {};
    (rows || []).forEach(r => { if (r.email) map[r.id] = r.email; });
    setEmailsById(map);
  } catch (error) {
    console.error('Erreur chargement utilisateurs:', error);
    alert('Erreur lors du chargement des utilisateurs');
  } finally {
    setLoading(false);
  }
}

async function toggleDemoStatus(userId) {
  const user = users.find(u => u.id === userId);
  const newDemoStatus = !user.is_demo;
  
  if (!window.confirm(`${newDemoStatus ? 'Marquer' : 'Retirer'} ${user.full_name || user.email} comme compte de démonstration ?`)) {
    return;
  }

  setActionLoading(userId);
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ is_demo: newDemoStatus })
      .eq('id', userId);

    if (error) throw error;

    setUsers(users.map(u => 
      u.id === userId ? { ...u, is_demo: newDemoStatus } : u
    ));

    alert(`✅ Statut démo ${newDemoStatus ? 'activé' : 'désactivé'}`);
  } catch (error) {
    console.error('Erreur mise à jour démo:', error);
    alert('❌ Erreur lors de la mise à jour');
  } finally {
    setActionLoading(null);
  }
}
async function handleUpdatePartnerType(userId, partnerType) {
  setActionLoading(userId);
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ partner_type: partnerType })
      .eq('id', userId);

    if (error) throw error;

    setUsers(users.map(u => 
      u.id === userId ? { ...u, partner_type: partnerType } : u
    ));

    alert('✅ Type de partenaire mis à jour');
  } catch (error) {
    console.error('Erreur:', error);
    alert('❌ Erreur lors de la mise à jour');
  } finally {
    setActionLoading(null);
  }
}
  async function loadRoleHistory() {
    try {
      const { data, error } = await supabase
        .from('role_change_history')
        .select(`
          *,
          changed_by_profile:changed_by(first_name, last_name)
        `)
        .order('changed_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Erreur chargement historique:', error);
        return;
      }

      // Les emails sont résolus au rendu via emailsById (alimenté par loadAllUsers) —
      // plus d'appel auth.admin.listUsers() (indisponible côté navigateur).
      setRoleHistory(data || []);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    }
  }

  async function sendRoleChangeEmail(user, oldRole, newRole) {
    try {
      const { error } = await supabase.functions.invoke('send-role-notification', {
        body: {
          to: user.email,
          userName: user.full_name || user.email,
          oldRole: oldRole || 'user',
          newRole: newRole,
          adminName: currentAdmin?.first_name && currentAdmin?.last_name
            ? `${currentAdmin.first_name} ${currentAdmin.last_name}`
            : 'Administrateur Artkiv'
        }
      });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Erreur envoi email:', error);
      return false;
    }
  }

  async function logRoleChange(userId, oldRole, newRole, reason = '') {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { error } = await supabase
        .from('role_change_history')
        .insert([{
          user_id: userId,
          old_role: oldRole || 'user',
          new_role: newRole,
          changed_by: session?.user.id,
          reason: reason
        }]);

      if (error) throw error;
    } catch (error) {
      console.error('Erreur logging changement rôle:', error);
    }
  }

  function handleRoleChangeRequest(userId, newRole) {
    const user = users.find(u => u.id === userId);
    const oldRole = user?.role;

    if (oldRole === newRole) {
      alert('Le rôle est déjà ' + newRole);
      return;
    }

    setConfirmDialog({
      show: true,
      userId: userId,
      newRole: newRole,
      userName: user.full_name || user.email,
      oldRole: oldRole || 'user'
    });
  }

  async function confirmRoleChange() {
    const { userId, newRole, oldRole } = confirmDialog;
    const user = users.find(u => u.id === userId);

    setConfirmDialog({ show: false, userId: null, newRole: null, userName: '', oldRole: '' });
    setActionLoading(userId);

    try {
      // Si on attribue le rôle expert, mettre approved à false pour confirmation ultérieure
      const updates = { role: newRole };
      if (newRole === 'expert') {
        updates.approved = false;
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId);

      if (updateError) throw updateError;

      await logRoleChange(userId, oldRole, newRole, '');
      const emailSent = await sendRoleChangeEmail(user, oldRole, newRole);

      setUsers(users.map(u => 
        u.id === userId ? { ...u, role: newRole, approved: newRole === 'expert' ? false : u.approved } : u
      ));

      await loadRoleHistory();

      const message = newRole === 'expert' 
        ? `✅ Rôle expert attribué\n⏳ L'expert doit maintenant être confirmé comme partenaire\n${emailSent ? '📧 Email envoyé' : '⚠️ Email non envoyé'}`
        : `✅ Rôle mis à jour avec succès : ${newRole}\n${emailSent ? '📧 Email envoyé' : '⚠️ Email non envoyé'}`;
      
      alert(message);
    } catch (error) {
      console.error('Erreur mise à jour rôle:', error);
      alert('❌ Erreur lors de la mise à jour du rôle');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleApprovePartner(userId) {
    const user = users.find(u => u.id === userId);
    
    if (!window.confirm(`Confirmer ${user.full_name || user.email} comme partenaire expert Artkiv ?\n\nCette action donnera accès à l'interface d'expertise.`)) {
      return;
    }

    setActionLoading(userId);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ approved: true })
        .eq('id', userId);

      if (error) throw error;

      setUsers(users.map(u => 
        u.id === userId ? { ...u, approved: true } : u
      ));

      alert('✅ Partenaire confirmé avec succès\n🎨 Accès à l\'interface expert activé');
    } catch (error) {
      console.error('Erreur confirmation partenaire:', error);
      alert('❌ Erreur lors de la confirmation');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRevokePartner(userId) {
    const user = users.find(u => u.id === userId);
    
    if (!window.confirm(`⚠️ ATTENTION : Révoquer le statut partenaire de ${user.full_name || user.email} ?\n\nCette action bloquera son accès à l'interface expert.`)) {
      return;
    }

    setActionLoading(userId);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ approved: false })
        .eq('id', userId);

      if (error) throw error;

      setUsers(users.map(u => 
        u.id === userId ? { ...u, approved: false } : u
      ));

      alert('🚫 Statut partenaire révoqué\n🔒 Accès à l\'interface expert bloqué');
    } catch (error) {
      console.error('Erreur révocation partenaire:', error);
      alert('❌ Erreur lors de la révocation');
    } finally {
      setActionLoading(null);
    }
  }

  function cancelRoleChange() {
    setConfirmDialog({ show: false, userId: null, newRole: null, userName: '', oldRole: '' });
  }

const filteredUsers = users.filter(user => {
  const matchesSearch = 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(searchTerm.toLowerCase());
  
  const matchesRole = filterRole === 'all' || user.role === filterRole;
  
  // Filtrer les comptes démo si la checkbox est décochée
  const matchesDemo = showDemo || !user.is_demo;

  return matchesSearch && matchesRole && matchesDemo;
});


  const roleColors = {
    admin: 'from-purple-500 to-purple-600',
    expert: 'from-blue-500 to-blue-600',
    team: 'from-green-500 to-green-600',
    user: 'from-gray-400 to-gray-500'
  };

  const roleLabels = {
    admin: 'Administrateur',
    expert: 'Expert',
    team: 'Équipe',
    user: 'Utilisateur'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Modal de confirmation */}
      {confirmDialog.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Confirmer le changement de rôle</h3>
            <div className="space-y-3 mb-6">
              <p className="text-gray-700">
                <span className="font-semibold">Utilisateur :</span> {confirmDialog.userName}
              </p>
              <div className="flex items-center gap-3 text-lg">
                <span className={`px-3 py-1 rounded-full text-white bg-gradient-to-r ${roleColors[confirmDialog.oldRole]}`}>
                  {roleLabels[confirmDialog.oldRole]}
                </span>
                <span className="text-gray-400">→</span>
                <span className={`px-3 py-1 rounded-full text-white bg-gradient-to-r ${roleColors[confirmDialog.newRole]}`}>
                  {roleLabels[confirmDialog.newRole]}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-3">
                📧 Un email de notification sera envoyé à l'utilisateur.
              </p>
              {confirmDialog.newRole === 'expert' && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    ℹ️ L'expert devra ensuite être confirmé comme partenaire pour accéder à l'interface d'expertise.
                  </p>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={cancelRoleChange}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-medium"
              >
                Annuler
              </button>
              <button
                onClick={confirmRoleChange}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all font-medium"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* En-tête */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">
              👥 Gestion des Experts
            </h2>
            <p className="text-sm text-gray-600">
              Attribuez le rôle "expert" pour donner accès à l'interface d'expertise
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 transition-all shadow-sm"
            >
              <span className="text-xl">📜</span>
              <span className="text-sm font-medium">
                {showHistory ? 'Masquer' : 'Voir'} l'historique
              </span>
            </button>
            <div className="bg-white rounded-xl px-6 py-3 shadow-sm border border-gray-200">
              <div className="text-3xl font-bold text-gray-900">{filteredUsers.length}</div>
              <div className="text-xs text-gray-500 mt-1">Utilisateurs</div>
            </div>
          </div>
        </div>
      </div>

      {/* Historique */}
      {showHistory && (
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            📜 Historique des changements de rôles
          </h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {roleHistory.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Aucun changement enregistré</p>
            ) : (
              roleHistory.map((change) => (
                <div
                  key={change.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r ${roleColors[change.old_role || 'user']}`}>
                        {roleLabels[change.old_role || 'user']}
                      </span>
                    </div>
                    <span className="text-gray-400">→</span>
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r ${roleColors[change.new_role]}`}>
                        {roleLabels[change.new_role]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0 ml-4">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {emailsById[change.user_id] || 'Utilisateur inconnu'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Par {emailsById[change.changed_by] || 'Admin'} • {new Date(change.changed_at).toLocaleString('fr-FR')}
                      </p>
                      {change.reason && (
                        <p className="text-xs text-gray-400 mt-1 italic">"{change.reason}"</p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🔍 Rechercher
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Email ou nom..."
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🎭 Filtrer par rôle
            </label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            >
              <option value="all">Tous les rôles</option>
              <option value="admin">Administrateurs</option>
              <option value="expert">Experts</option>
              <option value="team">Équipe</option>
              <option value="user">Utilisateurs</option>
            </select>
          </div>
        </div>
      </div>

{/* Liste des utilisateurs */}
<div className="space-y-3">
  {filteredUsers.length === 0 ? (
    <div className="bg-white rounded-2xl p-12 text-center border border-gray-200">
      <div className="text-5xl mb-4">🔍</div>
      <p className="text-gray-500">Aucun utilisateur trouvé</p>
    </div>
  ) : (
    filteredUsers.map((user) => (
      <div
        key={user.id}
        className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${roleColors[user.role || 'user']} flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
              {(user.full_name || user.email)?.[0]?.toUpperCase() || '?'}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {user.full_name || user.email}
                </h3>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r ${roleColors[user.role || 'user']} shadow-sm`}>
                  {roleLabels[user.role || 'user']}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1 truncate">{user.email}</p>
              {user.company && (
                <p className="text-xs text-gray-400 mt-1">🏢 {user.company}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 ml-4">
            <select
              value={user.role || 'user'}
              onChange={(e) => handleRoleChangeRequest(user.id, e.target.value)}
              disabled={actionLoading === user.id}
              className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all disabled:opacity-50"
            >
              <option value="user">Utilisateur</option>
              <option value="team">Équipe</option>
              <option value="expert">Expert</option>
              <option value="admin">Administrateur</option>
            </select>

            {actionLoading === user.id && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            )}
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-4 text-xs">
          <span className="text-gray-500">📅 Créé le {new Date(user.created_at).toLocaleDateString('fr-FR')}</span>

          {user.role === 'expert' ? (
            <>
              {user.approved ? (
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                  ✅ Partenaire confirmé
                </span>
              ) : (
                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full font-medium">
                  ⏳ En attente de confirmation
                </span>
              )}
            </>
          ) : (
            <>
              {user.plan && (
                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                  💎 Plan: {user.plan}
                </span>
              )}
              {!user.stripe_subscription_status && user.approved === false && (
                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full font-medium">
                  💳 En attente de paiement
                </span>
              )}
              {user.stripe_subscription_status === 'active' && user.approved && (
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                  ✅ Actif
                </span>
              )}
            </>
          )}

          {user.company && (
            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
              🏢 {user.company}
            </span>
          )}
        </div>

        {/* Sélecteur de type de partenaire pour les experts */}
        {user.role === 'expert' && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de partenaire
            </label>
            <select
              value={user.partner_type || ''}
              onChange={(e) => handleUpdatePartnerType(user.id, e.target.value)}
              className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={actionLoading === user.id}
            >
              <option value="">Non défini</option>
              <option value="expert_valuation">🎨 Expert Estimation & Validation</option>
              <option value="transport">🚚 Partenaire Transport</option>
              <option value="insurance">🛡️ Partenaire Assurance</option>
              <option value="other">📋 Autre</option>
            </select>
          </div>
        )}

        {/* Actions démo */}
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => toggleDemoStatus(user.id)}
            className="px-3 py-1.5 text-xs bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-all font-medium"
            disabled={actionLoading === user.id}
          >
            {user.is_demo ? '🎬 Retirer statut démo' : '🎬 Marquer comme démo'}
          </button>
        </div>

        {/* Actions expert */}
        {user.role === 'expert' && (
          <div className="mt-3 flex gap-2">
            {user.approved ? (
              <button
                onClick={() => handleRevokePartner(user.id)}
                className="px-3 py-1.5 text-xs bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-all font-medium"
                disabled={actionLoading === user.id}
              >
                🚫 Révoquer le statut partenaire
              </button>
            ) : (
              <button
                onClick={() => handleApprovePartner(user.id)}
                className="px-3 py-1.5 text-xs bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-all font-medium"
                disabled={actionLoading === user.id}
              >
                ✅ Confirmer comme partenaire
              </button>
            )}
          </div>
        )}
      </div>
    ))
  )}
</div>

      {/* Statistiques */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">📊 Statistiques</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-2xl font-bold text-purple-600">
              {users.filter(u => u.role === 'admin').length}
            </div>
            <div className="text-xs text-gray-600 mt-1">Administrateurs</div>
          </div>
          
          {/* Experts avec statut */}
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-2xl font-bold text-blue-600">
              {users.filter(u => u.role === 'expert' && u.approved).length}
            </div>
            <div className="text-xs text-gray-600 mt-1">✅ Experts actifs</div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-2xl font-bold text-amber-600">
              {users.filter(u => u.role === 'expert' && !u.approved).length}
            </div>
            <div className="text-xs text-gray-600 mt-1">⏳ Experts en attente</div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-2xl font-bold text-green-600">
              {users.filter(u => u.role === 'team').length}
            </div>
            <div className="text-xs text-gray-600 mt-1">Équipe</div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-2xl font-bold text-gray-600">
              {users.filter(u => !u.role || u.role === 'user').length}
            </div>
            <div className="text-xs text-gray-600 mt-1">Utilisateurs</div>
          </div>
        </div>
      </div>
{/* Section démos */}
  {users.filter(u => u.is_demo).length > 0 && (
    <>
      <div className="border-t border-gray-200 my-4"></div>
      <div className="bg-gradient-to-r from-orange-50 to-pink-50 rounded-xl p-4 border border-orange-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🎬</span>
            <span className="text-sm font-semibold text-gray-700">Comptes de démonstration</span>
          </div>
          <div className="text-2xl font-bold text-orange-600">
            {users.filter(u => u.is_demo).length}
          </div>
        </div>
      </div>
    </>
  )}



    </div>
  );
}